import React, { FC } from 'react';
import useCloseFlyouts from 'helpers/hooks/useCloseFlyouts';
import { useFormat } from 'helpers/hooks/useFormat';
import { CartSlideoutProps } from './type';
import CartItem from '../cart/components/cart-item';
import { EmptyState } from '../empty-state';
import OrderPaymentSection from '../order-payment-section';
import CheckoutButton from '../order-summary/components/checkout-button';
import { useGTM } from 'frontastic';

const CartSlideout: FC<CartSlideoutProps> = ({
  cart,
  isEmpty,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  emptyStateTitle,
  emptyStateSubtitle,
  emptyStateCategories,
  handleCategoryClick,
  onRemoveItem,
  onUpdateItem,
  sideCartPaymentMethods,
}) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { cartUpdatedEvent } = useGTM();

  const closeFlyouts = useCloseFlyouts();

  const handleUpdateItem = async (lineItemId: string, customLineItemId: string | undefined, quantity: number) => {
    const result = await onUpdateItem(lineItemId, customLineItemId, quantity);

    if (result && !result.isError) {
      cartUpdatedEvent();
    } else {
      console.error('Failed to update cart item:', result?.error);
    }
  };

  return (
    <>
      {isEmpty ? (
        <EmptyState
          className="grow"
          categories={emptyStateCategories}
          title={emptyStateTitle}
          subtitle={emptyStateSubtitle}
          handleCategoryClick={handleCategoryClick}
        />
      ) : (
        <div className="h-[65vh] grow divide-y divide-stroke-primary overflow-auto px-32">
          {cart?.lineItems?.map((lineItem) => (
            <CartItem
              key={lineItem.lineItemId}
              item={lineItem}
              onRemoveItem={() => onRemoveItem(lineItem.lineItemId as string, lineItem.customLineItemId)}
              onUpdateItem={(quantity) =>
                handleUpdateItem(lineItem.lineItemId as string, lineItem.customLineItemId, quantity)
              }
            />
          ))}
        </div>
      )}
      <OrderPaymentSection
        discounts={cart?.discountCodes ?? []}
        onApplyDiscountCode={onApplyDiscountCode}
        onRemoveDiscountCode={onRemoveDiscountCode}
        paymentMethods={sideCartPaymentMethods}
        isSideCart={true}
        isEmpty={isEmpty}
        className="px-32"
        classNames={{
          applyDiscountButton: 'px-8 pt-16 pb-12',
          infoContainer: 'py-16',
          totalAmount: 'pb-12',
          subCost: 'body-text text-primary',
        }}
        button={
          <CheckoutButton
            disabled={isEmpty}
            text={formatCartMessage({ id: 'cart.go', defaultMessage: 'Go to cart' })}
            link="/cart"
            onClick={closeFlyouts}
          />
        }
      />
    </>
  );
};

export default CartSlideout;
